import { useCallback, useEffect, useState } from 'react'

import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'

import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import {
    AddressLookupTableAccount,
    Connection,
    PublicKey,
    TransactionInstruction,
    TransactionMessage,
    VersionedTransaction,
} from '@solana/web3.js'

import { ResultCode, StepNames } from '@src/enums'
import { analyticsService } from '@src/services/analytics'
import { IJupiterQuoteResponse, JupiterOrderStatus } from '@src/types/jupiter'
import { createContextLogger } from '@src/utils/logger'
import { useApplicationStore, useHistoryStore } from '@src/zustand_stores'

const logger = createContextLogger('useJupiterSwap')

// Interface for the Jupiter swap callbacks
interface JupiterSwapCallbacks {
    onSwapStarted?: (data: { transactionId: string }) => void
    onSwapStatusChanged?: (status: JupiterOrderStatus) => void
    onSwapCompleted?: (signature: string) => void
    onSwapFailed?: (error: Error) => void
}

/**
 * Hook for handling Jupiter swap operations directly on Solana blockchain
 */
export const useJupiterSwap = (jupiterCallbacks?: JupiterSwapCallbacks) => {
    const { connection } = useConnection()
    const wallet = useWallet()

    // Store references
    const { setExchangeId, setCurrentModalId } = useApplicationStore()
    const { updateHistory } = useHistoryStore()

    // Swap state
    const [isSwapping, setIsSwapping] = useState(false)
    const [transactionId, setTransactionId] = useState<string | null>(null)
    const [swapStatus, setSwapStatus] = useState<JupiterOrderStatus | null>(null)
    const [signature, setSignature] = useState<string | null>(null)

    /**
     * Create a versioned transaction from Jupiter quote data
     * The quote response already contains all the transaction data needed
     */
    // Use Helius RPC for better reliability and higher rate limits
    const HELIUS_RPC_URL = 'https://nora-fpasy7-fast-mainnet.helius-rpc.com/'

    const createTransaction = useCallback(
        async (quoteData: IJupiterQuoteResponse): Promise<VersionedTransaction | null> => {
            // Log detailed information about the quote data for debugging
            logger.info('Creating transaction from Jupiter quote data', {
                hasQuoteData: !!quoteData,
                quoteDataKeys: quoteData ? Object.keys(quoteData) : [],
                hasTxData: quoteData && !!quoteData.tx_data,
                txDataKeys: quoteData && quoteData.tx_data ? Object.keys(quoteData.tx_data) : [],
                hasJupiterQuote: quoteData && !!quoteData.jupiter_quote,
                jupiterQuoteKeys: quoteData && quoteData.jupiter_quote ? Object.keys(quoteData.jupiter_quote) : [],
            })

            if (!quoteData) {
                logger.error('Cannot create transaction: missing quote data')
                return null
            }

            if (!quoteData.tx_data) {
                logger.error('Cannot create transaction: missing tx_data in quote', {
                    quoteDataType: typeof quoteData,
                    quoteDataKeys: Object.keys(quoteData),
                })
                return null
            }

            try {
                const { tx_data } = quoteData

                // Create a connection to Helius RPC for better reliability
                const heliusConnection = new Connection(HELIUS_RPC_URL, {
                    commitment: 'confirmed',
                    disableRetryOnRateLimit: false,
                })

                // Create lookup tables from the provided data
                const addressLookupTableAccounts: AddressLookupTableAccount[] = []

                if (tx_data.addressLookupTableAddresses && tx_data.addressLookupTableAddresses.length > 0) {
                    for (const address of tx_data.addressLookupTableAddresses) {
                        try {
                            // Use Helius connection instead of the default one
                            const lookupTable = await heliusConnection.getAddressLookupTable(new PublicKey(address))
                            if (lookupTable?.value) {
                                addressLookupTableAccounts.push(lookupTable.value)
                            }
                        } catch (err) {
                            logger.error(`Error fetching lookup table ${address}:`, err)
                        }
                    }
                }

                // Collect all instructions
                const instructions: TransactionInstruction[] = []

                // Add compute budget instructions if available
                if (tx_data.computeBudgetInstructions && tx_data.computeBudgetInstructions.length > 0) {
                    for (const instr of tx_data.computeBudgetInstructions) {
                        const accounts =
                            instr.accounts?.map((acc: { pubkey: string; isSigner: boolean; isWritable: boolean }) => ({
                                pubkey: new PublicKey(acc.pubkey),
                                isSigner: acc.isSigner,
                                isWritable: acc.isWritable,
                            })) || []

                        instructions.push(
                            new TransactionInstruction({
                                programId: new PublicKey(instr.programId),
                                keys: accounts,
                                data: Buffer.from(instr.data, 'base64'),
                            })
                        )
                    }
                }

                // Add setup instructions if available
                if (tx_data.setupInstructions && tx_data.setupInstructions.length > 0) {
                    for (const instr of tx_data.setupInstructions) {
                        const accounts = instr.accounts.map(
                            (acc: { pubkey: string; isSigner: boolean; isWritable: boolean }) => ({
                                pubkey: new PublicKey(acc.pubkey),
                                isSigner: acc.isSigner,
                                isWritable: acc.isWritable,
                            })
                        )

                        instructions.push(
                            new TransactionInstruction({
                                programId: new PublicKey(instr.programId),
                                keys: accounts,
                                data: Buffer.from(instr.data, 'base64'),
                            })
                        )
                    }
                }

                // Add swap instruction
                if (tx_data.swapInstruction) {
                    const swapAccounts = tx_data.swapInstruction.accounts.map(
                        (acc: { pubkey: string; isSigner: boolean; isWritable: boolean }) => ({
                            pubkey: new PublicKey(acc.pubkey),
                            isSigner: acc.isSigner,
                            isWritable: acc.isWritable,
                        })
                    )

                    instructions.push(
                        new TransactionInstruction({
                            programId: new PublicKey(tx_data.swapInstruction.programId),
                            keys: swapAccounts,
                            data: Buffer.from(tx_data.swapInstruction.data, 'base64'),
                        })
                    )
                }

                // Add cleanup instruction if available
                if (tx_data.cleanupInstruction) {
                    const cleanupAccounts = tx_data.cleanupInstruction.accounts.map(
                        (acc: { pubkey: string; isSigner: boolean; isWritable: boolean }) => ({
                            pubkey: new PublicKey(acc.pubkey),
                            isSigner: acc.isSigner,
                            isWritable: acc.isWritable,
                        })
                    )

                    instructions.push(
                        new TransactionInstruction({
                            programId: new PublicKey(tx_data.cleanupInstruction.programId),
                            keys: cleanupAccounts,
                            data: Buffer.from(tx_data.cleanupInstruction.data, 'base64'),
                        })
                    )
                }

                // Create transaction message
                const messageV0 = new TransactionMessage({
                    payerKey: wallet.publicKey as PublicKey,
                    recentBlockhash: tx_data.recentBlockhash,
                    instructions,
                }).compileToV0Message(addressLookupTableAccounts)

                // Create versioned transaction
                const transaction = new VersionedTransaction(messageV0)

                logger.info('Transaction created successfully from quote data', {
                    prioritizationFeeLamports: tx_data.prioritizationFeeLamports,
                })

                return transaction
            } catch (error) {
                logger.error('Error creating transaction from quote data:', error)
                return null
            }
        },
        [connection, wallet.publicKey]
    )

    /**
     * Execute a Jupiter swap directly on Solana blockchain
     */
    const executeJupiterSwap = useCallback(
        async (quoteData: IJupiterQuoteResponse): Promise<string | null> => {
            if (!wallet.publicKey || !wallet.signTransaction) {
                toast.error('Wallet not connected or does not support signing')
                return null
            }

            // Common variables used throughout the function
            let newExchangeId = ''
            const startTime = Date.now()
            
            // Extract data for analytics tracking (used across all cases)
            const sourceToken = quoteData.src_token?.symbol || ''
            const destinationToken = quoteData.dst_token?.symbol || ''
            const chainId = quoteData.src_token?.chain_id || ''
            
            // Get token prices with fallbacks (ensure we have non-zero values)
            let srcTokenPrice = quoteData.src_token?.price || 0
            let dstTokenPrice = quoteData.dst_token?.price || 0
            
            // Use hardcoded price values for common tokens if prices are zero/missing
            if (srcTokenPrice <= 0) {
                if (sourceToken === 'SOL') srcTokenPrice = 20.0  // Default fallback price
                else if (sourceToken === 'USDC' || sourceToken === 'USDT') srcTokenPrice = 1.0
                logger.warn(`Using fallback price for ${sourceToken}: ${srcTokenPrice}`)
            }
            
            if (dstTokenPrice <= 0) {
                if (destinationToken === 'SOL') dstTokenPrice = 20.0  // Default fallback price
                else if (destinationToken === 'USDC' || destinationToken === 'USDT') dstTokenPrice = 1.0
                logger.warn(`Using fallback price for ${destinationToken}: ${dstTokenPrice}`)
            }
            
            // Extract token decimals
            const srcDecimals = quoteData.src_token?.decimals || (sourceToken === 'SOL' ? 9 : 6)
            const dstDecimals = quoteData.dst_token?.decimals || (destinationToken === 'SOL' ? 9 : 6)
            
            // Parse amounts with safer handling of edge cases
            const sourceAmount = quoteData.src_token_quantity || '0'
            const destinationAmount = quoteData.dst_token_quantity || '0'
            
            // Safely calculate human-readable amounts with proper decimal adjustments
            let sourceAmountFloat = 0
            try {
                sourceAmountFloat = parseFloat(sourceAmount) / Math.pow(10, srcDecimals)
                // Handle potential NaN or Infinity
                if (!isFinite(sourceAmountFloat)) sourceAmountFloat = 0
            } catch (e) {
                logger.error('Error calculating sourceAmountFloat:', e)
                sourceAmountFloat = 0
            }
            
            let destinationAmountFloat = 0
            try {
                destinationAmountFloat = parseFloat(destinationAmount) / Math.pow(10, dstDecimals)
                // Handle potential NaN or Infinity
                if (!isFinite(destinationAmountFloat)) destinationAmountFloat = 0
            } catch (e) {
                logger.error('Error calculating destinationAmountFloat:', e)
                destinationAmountFloat = 0
            }
            
            // Calculate USD values with a minimum threshold to prevent zero values for small amounts
            const sourceAmountUSD = Math.max(sourceAmountFloat * srcTokenPrice, 0.01) // Ensure at least 1 cent
            const destinationAmountUSD = Math.max(destinationAmountFloat * dstTokenPrice, 0.01) // Ensure at least 1 cent
            
            // Log detailed debug information
            logger.debug('Analytics value calculation (detailed):', {
                sourceToken,
                sourceTokenPrice: srcTokenPrice,
                sourceDecimals: srcDecimals,
                sourceAmount,
                sourceAmountFloat,
                sourceAmountUSD,
                
                destinationToken,
                destinationTokenPrice: dstTokenPrice,
                destinationDecimals: dstDecimals,
                destinationAmount,
                destinationAmountFloat,
                destinationAmountUSD,
                
                quoteDataKeys: Object.keys(quoteData),
                hasSrcToken: !!quoteData.src_token,
                hasSrcPrice: quoteData.src_token && 'price' in quoteData.src_token,
                srcTokenKeys: quoteData.src_token ? Object.keys(quoteData.src_token) : [],
                hasDstToken: !!quoteData.dst_token,
                hasDstPrice: quoteData.dst_token && 'price' in quoteData.dst_token,
                dstTokenKeys: quoteData.dst_token ? Object.keys(quoteData.dst_token) : [],
            })
            
            const walletAddress = wallet.publicKey ? wallet.publicKey.toString() : ''
            
            // Extract Jupiter route details for analytics
            const jupiterRouteDetails = {
                routePlanLength: quoteData.jupiter_quote?.routePlan?.length || 0,
                ammNames: quoteData.jupiter_quote?.routePlan?.map(route => route.swapInfo?.label || '') || [],
                priceImpactPct: quoteData.jupiter_quote?.priceImpactPct || '0',
                prioritizationFeeLamports: quoteData.tx_data?.prioritizationFeeLamports || 0
            }

            try {
                setIsSwapping(true)
                setSwapStatus(JupiterOrderStatus.PROCESSING)

                // Create a new exchange ID for tracking
                newExchangeId = uuidv4()
                setExchangeId(newExchangeId)
                setCurrentModalId(newExchangeId)

                // Track Jupiter swap initiated
                analyticsService.trackJupiterSwapInitiated(
                    sourceToken,
                    destinationToken,
                    chainId,
                    sourceAmount,
                    sourceAmountUSD,
                    walletAddress,
                    jupiterRouteDetails
                )

                // Update history to show we're starting the swap
                updateHistory(newExchangeId, {
                    currentStep: StepNames.CONFIRM_SWAP,
                })

                // Create the transaction from quote data
                const transaction = await createTransaction(quoteData)

                if (!transaction) {
                    throw new Error('Failed to create transaction from quote data')
                }

                // Sign the transaction
                const signedTransaction = await wallet.signTransaction(transaction)

                // Update history to show swap is pending
                setSwapStatus(JupiterOrderStatus.SENDING_TO_DESTINATION)
                updateHistory(newExchangeId, {
                    currentStep: StepNames.MATCHING,
                    result: {
                        status: ResultCode.pending,
                        message: 'Transaction is being processed...',
                    },
                })

                // Wait a random short time to show the MATCHING step (100-250ms)
                await new Promise(resolve => setTimeout(resolve, Math.floor(Math.random() * 150) + 100))

                // Update to MATCH_CREATED step
                updateHistory(newExchangeId, {
                    currentStep: StepNames.MATCH_CREATED,
                    result: {
                        status: ResultCode.pending,
                        message: 'Transaction is being processed...',
                    },
                })

                // Create a connection to Helius RPC for better reliability
                const heliusConnection = new Connection(HELIUS_RPC_URL, {
                    commitment: 'confirmed',
                    disableRetryOnRateLimit: false,
                })

                // Send the transaction with optimized options using Helius connection
                const signature = await heliusConnection.sendTransaction(signedTransaction, {
                    maxRetries: 2,
                    skipPreflight: true,
                })

                setSignature(signature)
                setTransactionId(signature)

                logger.info('Jupiter swap transaction sent:', { signature })

                // Notify callback if provided
                if (jupiterCallbacks?.onSwapStarted) {
                    jupiterCallbacks.onSwapStarted({ transactionId: signature })
                }

                // Wait for confirmation with more detailed error handling using Helius connection
                const confirmation = await heliusConnection.confirmTransaction(signature)

                // Add a random short delay to ensure the MATCH_CREATED step is visible (200-350ms)
                await new Promise(resolve => setTimeout(resolve, Math.floor(Math.random() * 150) + 200))

                // Update to MATCH_EXECUTED step (still pending until we verify success)
                updateHistory(newExchangeId, {
                    currentStep: StepNames.MATCH_EXECUTED,
                    result: {
                        status: ResultCode.pending,
                        message: 'Finalizing transaction...',
                        sourceTxHash: signature,
                    },
                })

                // Add a short delay to show the MATCH_EXECUTED pending state
                await new Promise(resolve => setTimeout(resolve, Math.floor(Math.random() * 150) + 150))

                if (confirmation.value.err) {
                    // Transaction failed with detailed error
                    const errorMessage = `Transaction failed: ${JSON.stringify(confirmation.value.err)}`
                    setSwapStatus(JupiterOrderStatus.FAILED)

                    // Extract error code if available
                    let errorCode = ''
                    try {
                        const errObj = JSON.parse(JSON.stringify(confirmation.value.err))
                        errorCode = errObj.InstructionError?.[1]?.Custom || ''
                    } catch (e) {
                        // If parsing fails, continue without error code
                    }

                    // Keep the current step as MATCH_EXECUTED but update status to failure
                    updateHistory(newExchangeId, {
                        currentStep: StepNames.MATCH_EXECUTED,
                        result: {
                            status: ResultCode.failure,
                            message: errorMessage,
                            sourceTxHash: signature,
                        },
                    })

                    // Track Jupiter swap failure in analytics

                    analyticsService.trackJupiterSwapFailed(
                        sourceToken,
                        destinationToken,
                        chainId,
                        sourceAmount,
                        sourceAmountUSD,
                        errorMessage,
                        errorCode,
                        signature,
                        walletAddress,
                        jupiterRouteDetails
                    )

                    // Short delay to show the failure state
                    await new Promise(resolve => setTimeout(resolve, 300))

                    // Then update to MATCH_TIMED_OUT for the final error state
                    updateHistory(newExchangeId, {
                        currentStep: StepNames.MATCH_TIMED_OUT,
                        result: {
                            status: ResultCode.failure,
                            message: errorMessage,
                            sourceTxHash: signature,
                        },
                    })

                    if (jupiterCallbacks?.onSwapFailed) {
                        jupiterCallbacks.onSwapFailed(new Error(errorMessage))
                    }

                    toast.error('Jupiter swap failed')
                    return null
                }

                // Transaction succeeded
                setSwapStatus(JupiterOrderStatus.COMPLETED)
                
                // Calculate execution time
                const executionTime = Date.now() - startTime
                
                // Track Jupiter swap completion in analytics
                analyticsService.trackJupiterSwapCompleted(
                    sourceToken,
                    destinationToken,
                    chainId,
                    sourceAmount,
                    sourceAmountUSD,
                    destinationAmount,
                    destinationAmountUSD,
                    signature,
                    walletAddress,
                    executionTime,
                    jupiterRouteDetails
                )
                
                // Track Jupiter performance metrics
                const priceImpactBps = parseFloat(quoteData.jupiter_quote?.priceImpactPct || '0') * 100 // Convert to basis points
                analyticsService.trackJupiterPerformance(
                    1, // For now, we only have one route
                    jupiterRouteDetails.routePlanLength,
                    executionTime,
                    priceImpactBps,
                    sourceToken,
                    destinationToken,
                    walletAddress
                )

                // Update the history with success status but don't call onSuccess yet
                // This ensures the modal stays open to show the SwapCompleted component
                // We're already in MATCH_EXECUTED step, just update the status to success
                updateHistory(newExchangeId, {
                    currentStep: StepNames.MATCH_EXECUTED,
                    result: {
                        status: ResultCode.success,
                        sourceTxHash: signature,
                        message: 'Transaction completed successfully!',
                    },
                })

                // Notify callback if provided
                if (jupiterCallbacks?.onSwapCompleted) {
                    jupiterCallbacks.onSwapCompleted(signature)
                }

                // Show success toast
                toast.success('Swap completed successfully')

                // Return the signature
                return signature
            } catch (error) {
                logger.error('Error executing Jupiter swap:', error)

                // Check if the error is due to user rejection
                const errorMessage = error instanceof Error ? error.message : 'Unknown error during swap'
                const isUserRejection =
                    errorMessage.includes('User rejected') ||
                    errorMessage.includes('Transaction cancelled') ||
                    errorMessage.includes('User denied') ||
                    errorMessage.includes('User rejected the request')

                setSwapStatus(JupiterOrderStatus.FAILED)

                // Only update the step to MATCH_TIMED_OUT if it's not a user rejection
                if (!isUserRejection) {
                    // First update to MATCH_EXECUTED with failure status
                    updateHistory(newExchangeId, {
                        currentStep: StepNames.MATCH_EXECUTED,
                        result: {
                            status: ResultCode.failure,
                            message: errorMessage,
                        },
                    })

                    // Short delay to show the failure state
                    await new Promise(resolve => setTimeout(resolve, 300))

                    // Then update to MATCH_TIMED_OUT for the final error state
                    updateHistory(newExchangeId, {
                        currentStep: StepNames.MATCH_TIMED_OUT,
                        result: {
                            status: ResultCode.failure,
                            message: errorMessage,
                        },
                    })

                    toast.error(`Failed to execute swap: ${errorMessage}`)
                } else {
                    // For user rejections, don't change the step or show an error toast
                    logger.info('User rejected the transaction, keeping current step')
                }

                if (jupiterCallbacks?.onSwapFailed) {
                    jupiterCallbacks.onSwapFailed(
                        error instanceof Error ? error : new Error('Unknown error during swap')
                    )
                }

                return null
            } finally {
                setIsSwapping(false)
            }
        },
        [connection, wallet, createTransaction, setExchangeId, setCurrentModalId, updateHistory, jupiterCallbacks]
    )

    /**
     * Check transaction status on-chain
     */
    const checkTransactionStatus = useCallback(
        async (txSignature: string): Promise<JupiterOrderStatus> => {
            try {
                if (!txSignature) {
                    return JupiterOrderStatus.FAILED
                }

                const status = await connection.getSignatureStatus(txSignature)

                if (!status || !status.value) {
                    return JupiterOrderStatus.PROCESSING
                }

                if (status.value.err) {
                    return JupiterOrderStatus.FAILED
                }

                if (status.value.confirmationStatus === 'finalized') {
                    return JupiterOrderStatus.COMPLETED
                }

                if (status.value.confirmationStatus === 'confirmed') {
                    return JupiterOrderStatus.SENDING_TO_DESTINATION
                }

                return JupiterOrderStatus.PROCESSING
            } catch (error) {
                logger.error('Error checking transaction status:', error)
                return JupiterOrderStatus.PROCESSING
            }
        },
        [connection]
    )

    // Effect to update status when transaction ID changes
    useEffect(() => {
        if (!transactionId) return

        let isSubscribed = true
        const intervalId = setInterval(() => {
            if (!isSubscribed) return

            // Use void to indicate we're intentionally ignoring the Promise
            void (async (): Promise<void> => {
                const status = await checkTransactionStatus(transactionId)
                if (isSubscribed) {
                    setSwapStatus(status)

                    if (jupiterCallbacks?.onSwapStatusChanged) {
                        jupiterCallbacks.onSwapStatusChanged(status)
                    }

                    // If we've reached a terminal state, clear the interval
                    if (status === JupiterOrderStatus.COMPLETED || status === JupiterOrderStatus.FAILED) {
                        clearInterval(intervalId)
                    }
                }
            })()
        }, 2000) // Check every 2 seconds

        return () => {
            isSubscribed = false
            clearInterval(intervalId)
        }
    }, [transactionId, checkTransactionStatus, jupiterCallbacks])

    return {
        isSwapping,
        transactionId,
        swapStatus,
        signature,
        executeJupiterSwap,
        checkTransactionStatus,
    }
}

/**
 * Map backend status to frontend status enum
 */
export const mapBackendToFrontendJupiterStatus = (backendStatus: string): JupiterOrderStatus => {
    const statusMap: Record<string, JupiterOrderStatus> = {
        AWAITING_DEPOSIT: JupiterOrderStatus.AWAITING_DEPOSIT,
        DEPOSIT_DETECTED: JupiterOrderStatus.DEPOSIT_DETECTED,
        PROCESSING: JupiterOrderStatus.PROCESSING,
        SENDING_TO_DESTINATION: JupiterOrderStatus.SENDING_TO_DESTINATION,
        COMPLETED: JupiterOrderStatus.COMPLETED,
        EXPIRED: JupiterOrderStatus.EXPIRED,
        FAILED: JupiterOrderStatus.FAILED,
    }

    return statusMap[backendStatus] || JupiterOrderStatus.AWAITING_DEPOSIT
}
